/**
 * LICHT ZWISCHEN SCENES ÜBERBLENDEN (Jans Fund 19.10.2026, zwei Erdkugeln
 * hintereinander): Jede Scene bringt ihr eigenes Licht mit. Beim Wechsel
 * stehen zwei Scenes eine Weile gleichzeitig im Bild — die alte fährt hinaus
 * oder blendet aus, die neue kommt herein. Ohne Ausgleich leuchten in dieser
 * Zeit beide Sonnen voll (die neue Kugel war überstrahlt), und die alte Kugel
 * wirft ihren Schatten auf die neue (harte Kante quer über die Kugel).
 *
 * Das Gewicht einer Scene läuft über die Überlappung mit einer anderen
 * Licht-Scene linear von 1 auf 0 bzw. von 0 auf 1 — in der Summe bleibt es
 * immer 1. Scenes ohne eigenes Licht zählen nicht: Kommt nach der Erdkugel ein
 * Diagramm, behält die Kugel bis zum Schluss ihr volles Licht.
 */

export interface SceneFenster {
    id: string;
    from: number;
    to: number;
    mitLicht: boolean;
}

interface Knoten {
    id?: string;
    type?: string;
    from?: number;
    to?: number;
    visible?: boolean;
    tplusSceneId?: string;
    layers?: Knoten[];
}

function enthaeltLicht(k: Knoten): boolean {
    if (k.visible === false) return false;
    if (k.type === "light") return true;
    return (k.layers ?? []).some(enthaeltLicht);
}

const cache = new WeakMap<object, SceneFenster[]>();

/** Alle Scene-Wurzeln (Gruppen mit tplusSceneId) mit Zeitfenster und Licht-Merker. */
export function sceneFenster(baum: Knoten[] | Knoten | null | undefined): SceneFenster[] {
    if (!baum || typeof baum !== "object") return [];
    const vorhanden = cache.get(baum);
    if (vorhanden) return vorhanden;
    const fenster: SceneFenster[] = [];
    const besuche = (k: Knoten) => {
        if (k.tplusSceneId && k.id && typeof k.from === "number" && typeof k.to === "number") {
            fenster.push({ id: k.id, from: k.from, to: k.to, mitLicht: enthaeltLicht(k) });
            return;
        }
        (k.layers ?? []).forEach(besuche);
    };
    (Array.isArray(baum) ? baum : [baum]).forEach(besuche);
    cache.set(baum, fenster);
    return fenster;
}

/** Gewicht 0…1 einer Scene zum Zeitpunkt t (s. Kopfkommentar). */
export function sceneLichtGewicht(f: SceneFenster, alle: SceneFenster[], t: number): number {
    if (!f.mitLicht) return 1;
    let ein = 0;
    let aus = 0;
    for (const o of alle) {
        if (o === f || !o.mitLicht) continue;
        // Vorgänger reicht in unseren Anfang hinein → wir blenden ein
        if (o.from < f.from && o.to > f.from) ein = Math.max(ein, Math.min(o.to, f.to) - f.from);
        // Nachfolger beginnt vor unserem Ende → wir blenden aus
        if (o.from > f.from && o.from < f.to) aus = Math.max(aus, f.to - o.from);
    }
    let w = 1;
    if (ein > 0) w = Math.min(w, (t - f.from) / ein);
    if (aus > 0) w = Math.min(w, (f.to - t) / aus);
    return Math.max(0, Math.min(1, w));
}

/** Gewichte aller Scene-Wurzeln zum Zeitpunkt t, nach Wurzel-id. */
export function sceneLichtGewichte(baum: Knoten[] | Knoten | null | undefined, t: number): Map<string, number> {
    const alle = sceneFenster(baum);
    const gewichte = new Map<string, number>();
    for (const f of alle) gewichte.set(f.id, sceneLichtGewicht(f, alle, t));
    return gewichte;
}
